import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../interceptors/axios';
import Cookies from 'js-cookie';
import { useToast } from './Toast';

const WorkoutTooltip = ({ workout }) => {
  const [show, setShow] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const ref = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (show && tooltipRef.current) {
      const rect = tooltipRef.current.getBoundingClientRect();
      setFlipped(rect.top < 0);
    }
  }, [show]);

  if (!workout) return null;

  return (
    <span
      ref={ref}
      style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => { setShow(false); setFlipped(false); }}
      onClick={() => setShow(!show)}
    >
      <span className="rb-workout-badge">WOD</span>
      {show && (
        <div
          ref={tooltipRef}
          className={`rb-workout-tooltip ${flipped ? 'rb-workout-tooltip--below' : 'rb-workout-tooltip--above'}`}
        >
          <div className={`rb-workout-arrow ${flipped ? 'rb-workout-arrow--below' : 'rb-workout-arrow--above'}`} />
          {workout}
        </div>
      )}
    </span>
  );
};

const DashboardInformation = () => {
  const { showToast, ToastContainer } = useToast();
  const [classes, setClasses] = useState([]);
  const [classes_to_enroll, setClasses_to_enroll] = useState([]);
  const [recurringSchedules, setRecurringSchedules] = useState([]);
  const [errorMessages, setErrorMessages] = useState([]);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    const fetchEnrolledClasses = async () => {
      try {
        const requestData = { regybox_token: Cookies.get('regybox_token') };
        const response = await axiosInstance.get('/get-enrolled-classes', { params: requestData });

        if (response.data.success) {
          setClasses(response.data.enrolled_classes);
          setClasses_to_enroll(response.data.classes_list_to_enroll);
          setRecurringSchedules(response.data.recurring_schedules || []);
          setErrorMessages([]);
        } else {
          setErrorMessages([response.data.message]);
        }
      } catch (error) {
        setErrorMessages(['Failed to load enrolled classes.']);
      }
    };

    fetchEnrolledClasses();
  }, []);

  const removeClass = async (classe_date, classe_hour, class_name) => {
    try {
      const formData = { date: classe_date, time: classe_hour, class_name };
      const response = await axiosInstance.post(`/remove-class`, formData);
      if (response.data.success === true) {
        window.location.reload();
      } else {
        showToast(response.data.message?.[0] || 'Failed to remove class');
      }
    } catch (error) {
      const msg = error.response?.data?.message?.[0] || 'Failed to remove class';
      showToast(msg);
    }
  };

  const removeRecurring = async (weekday, hour, class_name) => {
    try {
      const formData = { weekday: weekday, time: hour, class_name };
      const response = await axiosInstance.post(`/remove-recurring-class`, formData);
      if (response.data.success === true) {
        window.location.reload();
      } else {
        showToast(response.data.message?.[0] || 'Failed to remove recurring schedule');
      }
    } catch (error) {
      const msg = error.response?.data?.message?.[0] || 'Failed to remove recurring schedule';
      showToast(msg);
    }
  };

  const removeClassFromRegybox = async (classe_date, classe_hour) => {
    try {
      const formData = {
        date: classe_date,
        time: classe_hour,
        regybox_token: Cookies.get('regybox_token')
      };
      const response = await axiosInstance.post(`/remove-class-from-regybox`, formData);
      if (response.data.success === true) {
        window.location.reload();
      } else {
        showToast(response.data.message?.[0] || 'Failed to remove class from Regybox');
      }
    } catch (error) {
      const msg = error.response?.data?.message?.[0] || 'Failed to remove class from Regybox';
      showToast(msg);
    }
  };

  return (
    <div className="rb-info-page">
      <ToastContainer />

      {/* ENROLLED IN REGYBOX */}
      <div className="rb-section">
        <div className="rb-section-header">
          <h2 className="rb-section-title">
            <span>Enrolled</span> Classes
          </h2>
        </div>

        {errorMessages.length > 0 ? (
          <div className="rb-error">{errorMessages}</div>
        ) : classes.length > 0 ? (
          <div>
            {classes.map((classItem, index) => (
              <div key={index} className="rb-list-item">
                <div className="rb-list-item-info">
                  {classItem.class_name && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span className="rb-list-item-name">{classItem.class_name}</span>
                      {classItem.room && <span className="rb-class-room">{classItem.room}</span>}
                      <WorkoutTooltip workout={classItem.workout} />
                    </div>
                  )}
                  <div className="rb-list-item-detail">
                    <span className="rb-list-item-detail-icon">&#128197;</span>
                    {classItem.date}
                  </div>
                  <div className="rb-list-item-detail">
                    <span className="rb-list-item-detail-icon">&#9201;</span>
                    {classItem.hour}
                  </div>
                  <div className="rb-list-item-detail">
                    <span className="rb-list-item-detail-icon">&#9679;</span>
                    {classItem.total_students} enrolled
                    {classItem.queue_position && (
                      <span className="rb-queue-position rb-queue-position--assured">
                        position #{classItem.queue_position}
                      </span>
                    )}
                  </div>
                  {classItem.coaches && classItem.coaches.length > 0 && (
                    <div className="rb-list-item-detail">
                      <span className="rb-list-item-detail-icon">&#127947;</span>
                      {classItem.coaches.join(", ")}
                    </div>
                  )}
                </div>
                <button
                  className="rb-remove-btn"
                  onClick={() => removeClassFromRegybox(classItem.date, classItem.hour)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="rb-empty">
            <div className="rb-empty-icon">&#128683;</div>
            <p>No classes enrolled at the moment</p>
          </div>
        )}
      </div>

      {/* SCHEDULED (ONE-TIME) */}
      <div className="rb-section">
        <div className="rb-section-header">
          <h2 className="rb-section-title">
            <span>Scheduled</span> Classes
          </h2>
          <button
            className="rb-enroll-btn"
            disabled={enrolling}
            onClick={async () => {
              setEnrolling(true);
              try {
                await axiosInstance.post('/trigger-enroll');
                setTimeout(() => window.location.reload(), 5000);
              } catch (error) {
                const msg = error.response?.data?.message?.[0] || 'Failed to trigger enrollment';
                showToast(msg);
                setEnrolling(false);
              }
            }}
          >
            {enrolling ? 'Enrolling...' : 'Enroll Now'}
          </button>
        </div>

        {errorMessages.length > 0 ? (
          <div className="rb-error">{errorMessages}</div>
        ) : classes_to_enroll.length > 0 ? (
          <div>
            {classes_to_enroll.map((classItem, index) => (
              <div key={index} className="rb-list-item">
                <div className="rb-list-item-info">
                  {classItem.class_name && (
                    <span className="rb-list-item-name">{classItem.class_name}</span>
                  )}
                  <div className="rb-list-item-detail">
                    <span className="rb-list-item-detail-icon">&#128197;</span>
                    {classItem.date}
                  </div>
                  <div className="rb-list-item-detail">
                    <span className="rb-list-item-detail-icon">&#9201;</span>
                    {classItem.hour}
                  </div>
                  {classItem.is_monitoring && classItem.queue_position && (
                    <div className="rb-list-item-detail">
                      <span className="rb-list-item-detail-icon">&#9679;</span>
                      <span className="rb-queue-position rb-queue-position--waitlist">
                        #{classItem.queue_position} in queue
                      </span>
                    </div>
                  )}
                  {classItem.is_enrolled && classItem.enrolled_minutes_after_open != null && (
                    <div className="rb-list-item-detail" title={`Enrolled: ${new Date(classItem.enrolled_at).toLocaleString()}\nSignup opened: ${new Date(classItem.signup_opened_at).toLocaleString()}`}>
                      <span className="rb-list-item-detail-icon">&#9989;</span>
                      <span className="rb-enrolled-timing">
                        enrolled {classItem.enrolled_minutes_after_open < 60
                          ? `${classItem.enrolled_minutes_after_open}min`
                          : `${Math.floor(classItem.enrolled_minutes_after_open / 60)}h${classItem.enrolled_minutes_after_open % 60}min`
                        } after signup opened
                      </span>
                    </div>
                  )}
                  {!classItem.is_enrolled && classItem.last_outcome && classItem.last_outcome !== "not_in_window" && (
                    <div className="rb-list-item-detail">
                      <span className="rb-list-item-detail-icon">
                        {classItem.last_outcome === "full" ? "\u{1F6AB}" :
                         classItem.last_outcome === "not_open" ? "\u{1F512}" :
                         classItem.last_outcome === "day_limit" ? "\u{1F4C5}" :
                         classItem.last_outcome === "blocked" ? "\u{1F6AB}" :
                         classItem.last_outcome === "waitlisted" ? "\u{23F3}" :
                         classItem.last_outcome === "login_failed" ? "\u{26A0}\u{FE0F}" :
                         "\u{1F916}"}
                      </span>
                      <span className={`rb-outcome rb-outcome--${classItem.last_outcome}`}>
                        {classItem.last_outcome === "full" ? "Class full" :
                         classItem.last_outcome === "not_open" ? "Booking not open yet" :
                         classItem.last_outcome === "day_limit" ? "Already booked another class that day" :
                         classItem.last_outcome === "blocked" ? "Blocked by plan limit" :
                         classItem.last_outcome === "waitlisted" ? "On waitlist" :
                         classItem.last_outcome === "login_failed" ? "Login failed" :
                         classItem.last_outcome}
                      </span>
                    </div>
                  )}
                  {!classItem.is_enrolled && classItem.signup_opened_at && (() => {
                    const openDate = new Date(classItem.signup_opened_at);
                    const now = new Date();
                    const diffMs = openDate - now;
                    const absMins = Math.abs(Math.round(diffMs / 60000));
                    let relative;
                    if (absMins < 60) relative = `${absMins}min`;
                    else if (absMins < 1440) relative = `${Math.floor(absMins / 60)}h${absMins % 60 ? absMins % 60 + 'min' : ''}`;
                    else relative = `${Math.floor(absMins / 1440)}d${Math.floor((absMins % 1440) / 60)}h`;
                    const label = diffMs > 0 ? `opens in ${relative}` : `opened ${relative} ago`;
                    return (
                      <div className="rb-list-item-detail" title={openDate.toLocaleString()}>
                        <span className="rb-list-item-detail-icon">&#128276;</span>
                        <span className="rb-signup-timing">{label}</span>
                      </div>
                    );
                  })()}
                </div>
                <button
                  className="rb-remove-btn"
                  onClick={() => removeClass(classItem.date, classItem.hour, classItem.class_name)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="rb-empty">
            <div className="rb-empty-icon">&#128203;</div>
            <p>No scheduled classes at the moment</p>
          </div>
        )}
      </div>

      {/* RECURRING WEEKLY */}
      <div className="rb-section">
        <div className="rb-section-header">
          <h2 className="rb-section-title">
            <span>Recurring</span> Weekly
          </h2>
        </div>

        {recurringSchedules.length > 0 ? (
          <div>
            {[...recurringSchedules]
              .sort((a, b) => a.weekday - b.weekday || a.hour.localeCompare(b.hour))
              .map((item, index) => (
              <div key={index} className="rb-list-item">
                <div className="rb-list-item-info">
                  {item.class_name && (
                    <span className="rb-list-item-name">{item.class_name}</span>
                  )}
                  <div className="rb-list-item-detail">
                    <span className="rb-list-item-detail-icon">&#128197;</span>
                    {item.weekday_name}
                  </div>
                  <div className="rb-list-item-detail">
                    <span className="rb-list-item-detail-icon">&#9201;</span>
                    {item.hour}
                  </div>
                </div>
                <button
                  className="rb-remove-btn"
                  onClick={() => removeRecurring(item.weekday, item.hour, item.class_name)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="rb-empty">
            <div className="rb-empty-icon">&#128260;</div>
            <p>No recurring schedules at the moment</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardInformation;
